// Classifies Apollo/naptime link errors that are expected operating conditions rather than
// application bugs, so shared Sentry capture points can skip reporting them. Intentionally does
// NOT filter HTTP 404 or 5xx -- those stay reported as genuine, actionable failures.
//
// Handles both error shapes that reach our capture points:
//  - the raw link error from catchLink (a transport-level fetch `TypeError`/`AbortError` with no status code,
//    or a `ServerError`/RestLink error carrying `statusCode`), and
//  - an Apollo `ApolloError` from a useQuery `onError` (with `networkError` / `graphQLErrors`).

type GraphQLErrorLike = { extensions?: { errorType?: string } | null };

type NetworkErrorLike = { name?: string; message?: string; statusCode?: number };

type MaybeApolloError = NetworkErrorLike & {
  networkError?: NetworkErrorLike | null;
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike | null | undefined>;
};

// Expected authorization failures. 404 (not found) and 5xx are deliberately excluded.
const SKIPPED_STATUS_CODES = new Set([401, 403]);

// The browser-specific messages a native fetch() rejection carries when there is no HTTP response
// (offline, DNS, CORS, navigation-abort): Chromium, Safari and Firefox respectively. A `TypeError` is
// only skipped when its message contains one of these, so a genuine `TypeError` bug is still reported.
const FETCH_FAILURE_MESSAGES = ['Failed to fetch', 'Load failed', 'NetworkError when attempting to fetch resource'];

const isFetchFailure = (error: NetworkErrorLike): boolean =>
  error.name === 'TypeError' && FETCH_FAILURE_MESSAGES.some((message) => error.message?.includes(message));

// A `DOMException` from an explicit `AbortController.abort()` -- a deliberate cancellation (e.g. Apollo
// tearing down an in-flight query on unmount), so never a bug.
const isAbort = (error: NetworkErrorLike): boolean => error.name === 'AbortError';

const isNonActionableNetworkError = (error?: NetworkErrorLike | null): boolean =>
  !!error && typeof error.statusCode !== 'number' && (isFetchFailure(error) || isAbort(error));

// A GraphQL-level authorization denial, identified by the gateway's structured `PERMISSION_DENIED`
// errorType extension. This is the gateway's contract for RBAC denials, so we key off it rather than
// matching on the (brittle, i18n-dependent) error message text.
const isGraphQLDenial = (graphQLError?: GraphQLErrorLike | null): boolean =>
  graphQLError?.extensions?.errorType === 'PERMISSION_DENIED';

export const isNonActionableApolloError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const apolloError = error as MaybeApolloError;

  const statusCode = apolloError.statusCode ?? apolloError.networkError?.statusCode;
  if (typeof statusCode === 'number' && SKIPPED_STATUS_CODES.has(statusCode)) {
    return true;
  }

  if (isNonActionableNetworkError(apolloError) || isNonActionableNetworkError(apolloError.networkError)) {
    return true;
  }

  // Only skip when EVERY GraphQL error is an authorization denial, so a mixed response that also
  // carries a genuine error is still reported.
  const { graphQLErrors } = apolloError;
  if (graphQLErrors && graphQLErrors.length > 0 && graphQLErrors.every(isGraphQLDenial)) {
    return true;
  }

  return false;
};
